<template>
  <div class="lc-link-ports" style="display: contents">
    <div
      v-if="showInputPort"
      class="lc-port lc-port--input panzoom-ignore"
      :class="{ 'lc-port--valid': isValidTarget, 'lc-port--invalid': !isValidTarget }"
      :title="isValidTarget ? 'Continue here' : 'Cannot link here — it would create a loop'"
      @click.stop="onInputPortClick">
      <v-icon x-small color="white">{{ isValidTarget ? "mdi-arrow-down-bold" : "mdi-cancel" }}</v-icon>
    </div>
    <div
      v-if="showOutputPort"
      class="lc-port lc-port--output panzoom-ignore"
      :class="{ 'lc-port--linked': hasNext, 'lc-port--active': isLinkSource }"
      :title="hasNext ? 'Remove link' : (isLinkSource ? 'Click a target, or here to cancel' : 'Link this branch to another message')"
      @click.stop="onOutputPortClick"
      @mouseenter="onOutputPortEnter"
      @mouseleave="onOutputPortLeave">
      <v-icon x-small color="white">{{ hasNext ? "mdi-close" : "mdi-link-variant" }}</v-icon>
    </div>
  </div>
</template>

<script>
  export default {
    name: "LiveChatLinkPorts",
    props: {
      node: {
        type: Object,
        required: true,
      },
    },
    inject: {
      linkContext: { default: null },
    },
    computed: {
      hasNext() {
        return !!this.node.next;
      },
      canBeSource() {
        return ["message", "actionItem", "decision"].includes(this.node.messageType);
      },
      canBeTarget() {
        return ["message", "action", "inputQuestion", "zipInputQuestion"].includes(this.node.messageType);
      },
      showOutputPort() {
        if (!this.linkContext) return false;
        if (!this.canBeSource) return false;
        if (this.node.children && this.node.children.length) return false;
        return true;
      },
      showInputPort() {
        if (!this.linkContext || !this.linkContext.state.sourceId) return false;
        if (!this.canBeTarget) return false;
        if (this.linkContext.state.sourceId === this.node.id) return false;
        return true;
      },
      isLinkSource() {
        return !!this.linkContext && this.linkContext.state.sourceId === this.node.id;
      },
      isValidTarget() {
        if (!this.linkContext) return false;
        return this.linkContext.isValidTarget(this.node.id);
      },
    },
    methods: {
      onOutputPortClick() {
        if (!this.linkContext) return;
        if (this.hasNext) {
          this.linkContext.removeLink(this.node.id);
          return;
        }
        if (this.isLinkSource) {
          this.linkContext.cancelLink();
        } else {
          this.linkContext.startLink(this.node.id);
        }
      },
      onOutputPortEnter() {
        if (!this.linkContext || !this.hasNext) return;
        this.linkContext.hoverLink(this.node.id);
      },
      onOutputPortLeave() {
        if (!this.linkContext) return;
        this.linkContext.hoverLink(null);
      },
      onInputPortClick() {
        if (!this.linkContext) return;
        this.linkContext.completeLink(this.node.id);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";

  .lc-port {
    position: absolute;
    left: 50%;
    width: 70px;
    height: 24px;
    border-radius: 0;
    transform: translateX(-50%);
    cursor: pointer;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;

    &--output {
      bottom: -12px;
      background: get-color("primary");
      border: 2px solid get-color("white");
      box-shadow: 0 0 0 1px get-color("primary");

      // Ring follows the fill, otherwise the lighter hover fill reads as the background dropping out.
      &:hover {
        background: get-color("primary-hover");
        box-shadow: 0 0 0 1px get-color("primary-hover");
      }

      // Compound selector: state is a separate class, so `&--active` (→ .lc-port--output--active) wouldn't match.
      &.lc-port--active,
      &.lc-port--active:hover {
        background: get-color("info");
        box-shadow: 0 0 0 1px get-color("info");
      }

      &.lc-port--linked {
        background: get-color("primary");
      }

      // Clicking a linked port removes the link, so it warns in the same red as a removable edge.
      &.lc-port--linked:hover {
        background: get-color("error");
        box-shadow: 0 0 0 1px get-color("error");
      }
    }

    &--input {
      top: -12px;
      background: get-color("white");

      &.lc-port--valid {
        background: get-color("success");
        border: 2px solid get-color("white");
        box-shadow: 0 0 0 2px get-color("success");

        &:hover {
          background: get-color("success-accent");
          box-shadow: 0 0 0 3px get-color("success-accent");
        }
      }
      &.lc-port--invalid {
        background: get-color("error");
        border: 2px solid get-color("white");
        box-shadow: 0 0 0 2px get-color("error");
        cursor: not-allowed;
      }
    }
  }
</style>
